import { ReactNode, useEffect, useMemo } from "react";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";

import useTypedContext from "hooks/useTypedContext";
import { isSaasDistribution } from "utils/distribution";
import { AccountContext } from "views/AccountWrapper";

import ProductSignUpForm from "./Form";
import useFirstUserSignUp from "./useFirstUserSignUp";
import { useLaunchpadRedirect } from "./useLaunchpadRedirect";
import { PSU_FORM_NAME } from "./constants";

const isPsuFormEnabled = process.env.REACT_APP_PSU_FORM_ENABLED === "true";

const isSaas = isSaasDistribution();

type ProductSignUpWrapperProps = {
  children: ReactNode;
};

const ProductSignUpWrapper = ({ children }: ProductSignUpWrapperProps) => {
  const { genericFormsList, viewer } = useTypedContext(AccountContext);
  const [searchParams] = useSearchParams();

  const hasDebugMode = searchParams.get("debug_psu_form") === "true";

  const shouldCompletePSUForm = useMemo(
    () =>
      isPsuFormEnabled &&
      ((viewer.isFirstUser && genericFormsList.some((form) => form.name === PSU_FORM_NAME)) ||
        hasDebugMode),
    [genericFormsList, viewer.isFirstUser, hasDebugMode]
  );

  const { shouldRedirectToLaunchpad, redirectToLaunchpad } = useLaunchpadRedirect();

  useFirstUserSignUp();

  useEffect(() => {
    if (!shouldCompletePSUForm && shouldRedirectToLaunchpad) {
      redirectToLaunchpad();
    }
  }, [redirectToLaunchpad, shouldCompletePSUForm, shouldRedirectToLaunchpad]);

  if (!isSaas || !isPsuFormEnabled) {
    return children;
  }

  return (
    <Routes>
      <Route
        path="welcome"
        element={shouldCompletePSUForm ? <ProductSignUpForm /> : <Navigate to="/" replace />}
      />
      <Route
        path="*"
        element={shouldCompletePSUForm ? <Navigate to="/welcome" replace /> : children}
      />
    </Routes>
  );
};

export default ProductSignUpWrapper;
